import { LangCode } from "@/lib/gql/__generated__/graphql";

import { useEditedQuestionIndicator, useErrorQuestionIndicator } from "../../../../_shared/hooks";
import { useSendCustomMailSettingForRequestConfirmed } from "../../../api/send-custom-mail-setting/request-confirmed";
import { useSendCustomMailSettingForRequestCreate } from "../../../api/send-custom-mail-setting/request-create";
import { useSendCustomMailSettingForRequestDeclined } from "../../../api/send-custom-mail-setting/request-declined";
import { MailTemplateItem } from "../_shared/mail-template-item";

import type { BasicCustomMailSettingFormData } from "../../../schema";
import type { UseFormReturn } from "react-hook-form";

type Props = {
	form: UseFormReturn<BasicCustomMailSettingFormData>;
	hotelID: string;
	hotelEmailAddress: string;
};

export const ForRequestReservation = ({ form, hotelID, hotelEmailAddress }: Props) => {
	const {
		watch,
		setValue,
		formState: { errors },
	} = form;

	const requestValue = watch("forReservation.atRequest.text");
	const confirmedValue = watch("forReservation.atConfirmed.text");
	const canceledValue = watch("forReservation.atDeclined.text");

	// 値が入っているかどうかを監視（設定した項目がある場合に青丸を表示）
	useEditedQuestionIndicator("リクエスト申請時", Boolean(requestValue));
	useEditedQuestionIndicator("施設がリクエストを承認した際", Boolean(confirmedValue));
	useEditedQuestionIndicator("施設がリクエストを承認しなかった場合", Boolean(canceledValue));

	// バリデーションエラーがある場合に赤丸を表示
	useErrorQuestionIndicator("リクエスト申請時", Boolean(errors.forReservation?.atRequest?.text));
	useErrorQuestionIndicator("施設がリクエストを承認した際", Boolean(errors.forReservation?.atConfirmed?.text));
	useErrorQuestionIndicator("施設がリクエストを承認しなかった場合", Boolean(errors.forReservation?.atDeclined?.text));

	const { sendTestMailAtRequestCreate } = useSendCustomMailSettingForRequestCreate();
	const { sendTestMailAtRequestConfirmed } = useSendCustomMailSettingForRequestConfirmed();
	const { sendTestMailAtRequestDeclined } = useSendCustomMailSettingForRequestDeclined();

	const handleSendTestEmailForRequest = async ({ email }: { email: string }) => {
		await sendTestMailAtRequestCreate({
			mailAddress: email,
			customMessage: requestValue || "",
			id: hotelID,
			langCode: LangCode.Default,
		});
	};

	const handleSendTestEmailForConfirmed = async ({ email }: { email: string }) => {
		await sendTestMailAtRequestConfirmed({
			mailAddress: email,
			customMessage: confirmedValue || "",
			id: hotelID,
			langCode: LangCode.Default,
		});
	};

	const handleSendTestEmailForDeclined = async ({ email }: { email: string }) => {
		await sendTestMailAtRequestDeclined({
			mailAddress: email,
			customMessage: canceledValue || "",
			id: hotelID,
			langCode: LangCode.Default,
		});
	};

	return (
		<div className="space-y-6">
			<div className="ml-2">
				{/* リクエスト申請時*/}
				<MailTemplateItem
					hidePreviewButton={false}
					label={"リクエスト申請時"}
					dialogTitle={"リクエスト申請時"}
					value={requestValue || ""}
					errorMessage={errors.forReservation?.atRequest?.text?.message || ""}
					handleChange={(value) =>
						setValue(
							"forReservation.atRequest.text", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
					sendTestEmail={handleSendTestEmailForRequest}
					lang={LangCode.Default}
					hotelEmailAddress={hotelEmailAddress}
				/>
				{/* リクエスト承認時 */}
				<MailTemplateItem
					hidePreviewButton={false}
					label={"施設がリクエストを承認した際"}
					dialogTitle={"施設がリクエストを承認した際"}
					value={confirmedValue || ""}
					errorMessage={errors.forReservation?.atConfirmed?.text?.message || ""}
					handleChange={(value) =>
						setValue(
							"forReservation.atConfirmed.text", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
					sendTestEmail={handleSendTestEmailForConfirmed}
					lang={LangCode.Default}
					hotelEmailAddress={hotelEmailAddress}
				/>
				{/* リクエスト非承認時 */}
				<MailTemplateItem
					hidePreviewButton={false}
					label={"施設がリクエストを承認しなかった場合"}
					dialogTitle={"施設がリクエストを承認しなかった場合"}
					value={canceledValue || ""}
					errorMessage={errors.forReservation?.atDeclined?.text?.message || ""}
					handleChange={(value) =>
						setValue(
							"forReservation.atDeclined.text", //
							value,
							{ shouldDirty: true, shouldValidate: true },
						)
					}
					sendTestEmail={handleSendTestEmailForDeclined}
					lang={LangCode.Default}
					hotelEmailAddress={hotelEmailAddress}
				/>
			</div>
		</div>
	);
};
